import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import {
  carbonMonthlyData,
  carbonByProjectData,
  carbonByAreaTypeData,
  carbonQualityMetrics,
  carbonAlerts,
  carbonNeighborhoodRank,
} from "@/mocks/carbono";

const COLORS = ["#1B6E4F", "#0052CC", "#64B5F6", "#CAF0D3", "#FBC02D"];

export default function CarbonOverview() {
  const monthlyData = carbonMonthlyData.map((d) => ({
    month: d.month,
    "CO2e (t)": d.co2e,
  }));

  const projectData = carbonByProjectData.map((d) => ({
    name: d.project.split(" ").slice(0, 2).join(" "),
    CO2e: d.co2e,
    Carbono: d.carbon,
  }));

  const areaData = carbonByAreaTypeData.map((d) => ({
    name: d.type,
    value: d.co2e,
    label: d.label,
  }));

  return (
    <div className="space-y-6">
      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Evolution */}
        <div className="bg-white rounded-xl border border-background-200/70 p-5">
          <h3 className="text-sm font-semibold text-foreground-900 mb-1 font-heading">Evolução estimada de CO2e</h3>
          <p className="text-xs text-foreground-500 mb-4">Estimativa preliminar acumulada por mês (t CO2e)</p>
          <div className="h-[260px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={monthlyData} margin={{ top: 5, right: 10, left: 10, bottom: 0 }}>
                <defs>
                  <linearGradient id="co2eGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#1B6E4F" stopOpacity={0.2} />
                    <stop offset="95%" stopColor="#1B6E4F" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E8EAED" />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#6B737A" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "#6B737A" }} axisLine={false} tickLine={false} width={50} />
                <Tooltip
                  contentStyle={{ borderRadius: "8px", border: "1px solid #E8EAED", fontSize: "12px" }}
                  formatter={(value: number) => [`${value.toLocaleString("pt-BR")} t`, "CO2e"]}
                />
                <Area type="monotone" dataKey="CO2e (t)" stroke="#1B6E4F" fill="url(#co2eGrad)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* By Project */}
        <div className="bg-white rounded-xl border border-background-200/70 p-5">
          <h3 className="text-sm font-semibold text-foreground-900 mb-1 font-heading">Carbono estimado por projeto</h3>
          <p className="text-xs text-foreground-500 mb-4">Comparativo de CO2e e Carbono (t)</p>
          <div className="h-[260px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={projectData} margin={{ top: 5, right: 10, left: 10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E8EAED" />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#6B737A" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "#6B737A" }} axisLine={false} tickLine={false} width={45} />
                <Tooltip
                  contentStyle={{ borderRadius: "8px", border: "1px solid #E8EAED", fontSize: "12px" }}
                />
                <Legend iconType="circle" wrapperStyle={{ fontSize: "11px" }} />
                <Bar dataKey="CO2e" fill="#1B6E4F" radius={[4, 4, 0, 0]} barSize={18} name="CO2e" />
                <Bar dataKey="Carbono" fill="#0052CC" radius={[4, 4, 0, 0]} barSize={18} name="Carbono (t C)" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* By Area Type */}
        <div className="bg-white rounded-xl border border-background-200/70 p-5">
          <h3 className="text-sm font-semibold text-foreground-900 mb-1 font-heading">CO2e por tipo de área</h3>
          <p className="text-xs text-foreground-500 mb-4">Distribuição preliminar por categoria ambiental</p>
          <div className="h-[260px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={areaData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={90}
                  paddingAngle={3}
                  dataKey="value"
                  label={(props: { name?: string; percent?: number }) => `${props.name} ${((props.percent ?? 0) * 100).toFixed(0)}%`}
                  labelLine={false}
                  style={{ fontSize: "11px", fill: "#3F4A52" }}
                >
                  {areaData.map((_, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{ borderRadius: "8px", border: "1px solid #E8EAED", fontSize: "12px" }}
                  formatter={(value: number, name: string) => [`${value.toLocaleString("pt-BR", { maximumFractionDigits: 1 })} t`, name]}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Neighborhood Rank */}
        <div className="bg-white rounded-xl border border-background-200/70 p-5">
          <h3 className="text-sm font-semibold text-foreground-900 mb-1 font-heading">Ranking por bairro/área</h3>
          <p className="text-xs text-foreground-500 mb-4">Maiores contribuições estimadas de CO2e</p>
          <div className="space-y-3">
            {carbonNeighborhoodRank.map((n, i) => (
              <div key={n.name} className="flex items-center gap-3">
                <div className="w-6 h-6 rounded-full bg-primary-50 text-primary-700 text-[11px] font-bold flex items-center justify-center shrink-0">
                  {i + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-medium text-foreground-800 truncate">{n.name}</span>
                    <span className="text-xs font-semibold text-primary-700">{n.co2e.toLocaleString("pt-BR", { maximumFractionDigits: 1 })} t CO2e</span>
                  </div>
                  <div className="w-full bg-background-200 rounded-full h-2">
                    <div
                      className="bg-primary-500 h-2 rounded-full"
                      style={{ width: `${(n.co2e / carbonNeighborhoodRank[0].co2e) * 100}%` }}
                    />
                  </div>
                  <div className="flex items-center justify-between mt-0.5">
                    <span className="text-[10px] text-foreground-400">{n.treesCount.toLocaleString("pt-BR")} árvores</span>
                    <span className="text-[10px] text-foreground-400">Confiança {n.avgConfidence}%</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Quality & Alerts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Quality Panel */}
        <div className="bg-white rounded-xl border border-background-200/70 p-5">
          <div className="flex items-center gap-2 mb-4">
            <i className="ri-shield-check-line text-primary-600 w-5 h-5 flex items-center justify-center" />
            <h3 className="text-sm font-semibold text-foreground-900 font-heading">Qualidade dos Dados</h3>
          </div>
          <div className="space-y-4">
            {carbonQualityMetrics.map((m) => (
              <div key={m.label}>
                <div className="flex items-center justify-between text-xs mb-1.5">
                  <span className="text-foreground-700">{m.label}</span>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-foreground-800">{m.value}{m.unit}</span>
                    {m.status === "bom" && <i className="ri-check-line text-primary-600 w-3.5 h-3.5 flex items-center justify-center" />}
                    {m.status === "regular" && <i className="ri-alert-line text-caution-600 w-3.5 h-3.5 flex items-center justify-center" />}
                    {m.status === "ruim" && <i className="ri-error-warning-line text-warning-600 w-3.5 h-3.5 flex items-center justify-center" />}
                  </div>
                </div>
                <div className="w-full bg-background-200 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full ${m.status === "bom" ? "bg-primary-500" : m.status === "regular" ? "bg-caution-500" : "bg-warning-500"}`}
                    style={{ width: `${m.value}%` }}
                  />
                </div>
                <div className="flex justify-between text-[10px] text-foreground-400 mt-0.5">
                  <span>0%</span>
                  <span>Meta: {m.target}{m.unit}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Alerts Panel */}
        <div className="bg-white rounded-xl border border-background-200/70 p-5">
          <div className="flex items-center gap-2 mb-4">
            <i className="ri-notification-3-line text-warning-600 w-5 h-5 flex items-center justify-center" />
            <h3 className="text-sm font-semibold text-foreground-900 font-heading">Alertas de Dados</h3>
          </div>
          <div className="space-y-3">
            {carbonAlerts.map((alert) => (
              <div
                key={alert.id}
                className={`rounded-lg p-3 border ${
                  alert.severity === "crítico"
                    ? "bg-danger-50 border-danger-100"
                    : alert.severity === "atenção"
                    ? "bg-warning-50 border-warning-100"
                    : "bg-caution-50 border-caution-100"
                }`}
              >
                <div className="flex items-start gap-2">
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${
                    alert.severity === "crítico"
                      ? "bg-danger-100 text-danger-600"
                      : alert.severity === "atenção"
                      ? "bg-warning-100 text-warning-600"
                      : "bg-caution-100 text-caution-600"
                  }`}>
                    <i className={`${
                      alert.severity === "crítico" ? "ri-error-warning-line" : "ri-alert-line"
                    } w-full h-full flex items-center justify-center text-xs`} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-0.5">
                      <span className="text-xs font-semibold text-foreground-800">{alert.type}</span>
                      <span className={`text-[11px] font-bold px-1.5 py-0.5 rounded ${
                        alert.severity === "crítico"
                          ? "bg-danger-100 text-danger-700"
                          : alert.severity === "atenção"
                          ? "bg-warning-100 text-warning-700"
                          : "bg-caution-100 text-caution-700"
                      }`}>
                        {alert.count.toLocaleString("pt-BR")}
                      </span>
                    </div>
                    <p className="text-xs text-foreground-600 leading-relaxed">{alert.message}</p>
                    <div className="text-[11px] text-primary-600 mt-1 font-medium">{alert.action}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}